import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader } from "../ui";
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import Modal from '../ui/Modal';
import FileUpload from '../ui/FileUpload';
import type { UnifiedContact } from '../../services/unifiedContactService';
import { unifiedContactService } from '../../services/unifiedContactService';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

import {
  autoSyncDistributionForAsset,
  removeAssetDistribution,
  type DistributionMap,
} from '../../utils/distributionUtils';
import {
  CreditCardIcon,
  CarIcon,
  HomeIcon,
  MedicalIcon,
  TaxIcon,
  BankIcon,
  LightBulbIcon,
  ShieldIcon,
  SubscriptionIcon,
  DocumentsIcon,
  ImportantDocumentsIcon,
  PhysicalAssetsIcon,
  CollectiblesAndValuablesIcon,
  ElectronicsIcon,
  NewDigitalAssetsIcon,
  CustomItemIcon
} from '../ui/Icons';

interface Beneficiary {
  contactId: string;
  name: string;
}

interface CashAccount {
  id: string;
  type: 'checking' | 'savings' | 'money_market' | 'cd' | 'other';
  name: string;
  institution: string;
  accountNumber: string;
  routingNumber?: string;
  balance?: number;
  interestRate?: number;
  maturityDate?: string;
  beneficiaries?: Beneficiary[];
  notes?: string;
  documents?: AppDocument[];
}

interface InvestmentAccount {
  id: string;
  type: 'stocks' | 'bonds' | 'mutual_funds' | 'etfs' | 'other';
  name: string;
  institution: string;
  accountNumber: string;
  notes?: string;
  documents?: AppDocument[];
}

interface RetirementAccount {
  id: string;
  type: 'traditional_ira' | '401k' | 'roth_ira' | 'pension' | 'other';
  name: string;
  institution: string;
  accountNumber: string;
  beneficiaries?: Beneficiary[];
  notes?: string;
  documents?: AppDocument[];
}

interface OtherFinancialAsset {
  id: string;
  type: 'life_insurance' | 'annuity' | 'trust' | 'business' | 'other';
  name: string;
  institution?: string;
  policyNumber?: string;
  beneficiaries?: Beneficiary[];
  notes?: string;
  documents?: AppDocument[];
}

interface Property {
  id: string;
  type: 'house' | 'apartment' | 'land' | 'commercial' | 'other';
  address: string;
  description?: string;
  purchasePrice?: number;
  currentValue?: number;
  mortgageBalance: number;
  monthlyPayment?: number;
  notes?: string;
  lat?: number;
  lon?: number;
  displayName?: string;
  documents?: AppDocument[];
}

interface PersonalItem {
  id: string;
  category: 'physical_assets' | 'collectibles' | 'electronics' | 'digital_assets' | 'custom';
  name: string;
  description: string;
  estimatedValue?: number;
  accountNumber?: string;
  location: string;
  handlingInstructions: string;
  notes: string;
  createdAt: string;
  documents?: AppDocument[];
  assetType?: 'vehicle' | 'property' | 'title' | 'other';
  digitalType?: 'social_media' | 'email' | 'digital_media' | 'membership' | 'other';
  collectibleType?: 'art' | 'antique' | 'jewelry' | 'watch' | 'other';
}

interface IncomeSourceItem {
  id: string;
  type: 'social_security' | 'pension' | 'rental_income' | 'investment_income' | 'business_income' | 'annuity' | 'other';
  sourceName: string;
  payerInstitution: string;
  frequency: 'monthly' | 'quarterly' | 'annual';
  approximateAmount?: number;
  notes?: string;
  documents?: AppDocument[];
}

interface DebtItem {
  id: string;
  category: 'credit_card' | 'personal_auto' | 'mortgage' | 'medical' | 'tax' | 'other';
  creditor: string;
  amount?: number;
  accountNumber?: string;
  dueDate?: string;
  notes?: string;
  documents?: AppDocument[];
}

interface ExpenseItem {
  id: string;
  category: 'credit_card' | 'utilities' | 'insurance' | 'subscriptions' | 'transportation' | 'healthcare' | 'other';
  name: string;
  amount?: number;
  frequency: 'monthly' | 'quarterly' | 'annually' | 'unknown';
  dueDate?: string;
  accountNumber?: string;
  autopay: boolean;
  provider?: string;
  website?: string;
  notes?: string;
  documents?: AppDocument[];
}

type MajorCategoryKey =
  | 'banking'
  | 'investment'
  | 'retirement'
  | 'other_financial'
  | 'properties'
  | 'personal'
  | 'debts'
  | 'monthly_expenses'
  | 'income_sources';

type FilterKey = 'all' | MajorCategoryKey;

type CategoryItem =
  | CashAccount
  | InvestmentAccount
  | RetirementAccount
  | OtherFinancialAsset
  | Property
  | PersonalItem
  | DebtItem
  | ExpenseItem
  | IncomeSourceItem;

/**
 * Unified list row shape used by the overview list.
 * Each category-specific item is normalized into this format for rendering/filtering.
 */
interface OverviewItem {
  id: string;
  categoryKey: MajorCategoryKey;
  categoryLabel: string;
  typeLabel: string;
  title: string;
  subtitle?: string;
  details: string[];
  notes?: string;
  documentsCount: number;
  raw: unknown;
}

const CATEGORY_LABELS: Record<MajorCategoryKey, string> = {
  banking: 'Banking',
  investment: 'Investment Accounts',
  retirement: 'Retirement Accounts',
  other_financial: 'Other Financial Assets',
  properties: 'Properties',
  personal: 'Personal Belongings',
  debts: 'Debts & Liabilities',
  monthly_expenses: 'Monthly Expenses',
  income_sources: 'Income Sources',
};

const MAJOR_CATEGORIES: { key: MajorCategoryKey; label: string }[] = [
  { key: 'banking', label: 'Banking' },
  { key: 'investment', label: 'Investment' },
  { key: 'retirement', label: 'Retirement' },
  { key: 'other_financial', label: 'Other Financial' },
  { key: 'properties', label: 'Properties' },
  { key: 'personal', label: 'Personal Belongings' },
  { key: 'debts', label: 'Debts' },
  { key: 'monthly_expenses', label: 'Monthly Expenses' },
  { key: 'income_sources', label: 'Income Sources' },
];

const BANKING_TYPES = [
  { value: 'checking', label: 'Checking Accounts' },
  { value: 'savings', label: 'Savings Accounts' },
  { value: 'money_market', label: 'Money Market Accounts' },
  { value: 'cd', label: 'Certificates of Deposit' },
  { value: 'other', label: 'Other Accounts' },
];

const INVESTMENT_TYPES = [
  { value: 'stocks', label: 'Stocks' },
  { value: 'bonds', label: 'Bonds' },
  { value: 'mutual_funds', label: 'Mutual Funds' },
  { value: 'etfs', label: 'ETFs' },
  { value: 'other', label: 'Other Investments' },
];

const RETIREMENT_TYPES = [
  { value: 'traditional_ira', label: 'Traditional IRA' },
  { value: '401k', label: '401(k)' },
  { value: 'roth_ira', label: 'Roth IRA' },
  { value: 'pension', label: 'Pension Plan' },
  { value: 'other', label: 'Other Retirement' },
];

const OTHER_FINANCIAL_TYPES = [
  { value: 'life_insurance', label: 'Life Insurance' },
  { value: 'annuity', label: 'Annuities' },
  { value: 'trust', label: 'Trust Funds' },
  { value: 'business', label: 'Business Interests' },
  { value: 'other', label: 'Other Assets' },
];

const PROPERTY_TYPES = [
  { value: 'house', label: 'House' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'land', label: 'Land' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'other', label: 'Other Property' },
];

const PERSONAL_TYPES = [
  { value: 'physical_assets', label: 'Physical Assets' },
  { value: 'collectibles', label: 'Collectibles & Valuables' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'digital_assets', label: 'Digital Assets' },
  { value: 'custom', label: 'Custom Item' },
];

const PHYSICAL_ASSET_SUBTYPES = [
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'property', label: 'Property' },
  { value: 'title', label: 'Title' },
  { value: 'other', label: 'Other' },
];

const DIGITAL_SUBTYPES = [
  { value: 'social_media', label: 'Social Media' },
  { value: 'email', label: 'Email' },
  { value: 'digital_media', label: 'Digital Media Services' },
  { value: 'membership', label: 'Membership' },
  { value: 'other', label: 'Other' },
];

const COLLECTIBLE_SUBTYPES = [
  { value: 'art', label: 'Art' },
  { value: 'antique', label: 'Antique' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'watch', label: 'Watch' },
  { value: 'other', label: 'Other' },
];

const DEBT_TYPES = [
  { value: 'credit_card', label: 'Credit Card Debt' },
  { value: 'personal_auto', label: 'Personal & Auto Loans' },
  { value: 'mortgage', label: 'Property Mortgages' },
  { value: 'medical', label: 'Medical Expenses' },
  { value: 'tax', label: 'Tax Debt' },
  { value: 'other', label: 'Other Loans' },
];

const EXPENSE_TYPES = [
  { value: 'credit_card', label: 'Credit Card Payments' },
  { value: 'utilities', label: 'Utility Bills' },
  { value: 'insurance', label: 'Insurance Premiums' },
  { value: 'subscriptions', label: 'Recurring Subscriptions' },
  { value: 'transportation', label: 'Transportation Expenses' },
  { value: 'healthcare', label: 'Healthcare Expenses' },
  { value: 'other', label: 'Other Expenses' },
];

const INCOME_SOURCE_TYPES = [
  { value: 'social_security',   label: 'Social Security' },
  { value: 'pension',           label: 'Pension' },
  { value: 'rental_income',     label: 'Rental Income' },
  { value: 'investment_income', label: 'Investment Income' },
  { value: 'business_income',   label: 'Business Income' },
  { value: 'annuity',           label: 'Annuity' },
  { value: 'other',             label: 'Other Income' },
];

/**
 * Returns the primary type options for the selected major category.
 * Used by the shared modal to switch form inputs by category.
 */
const getTypeOptions = (category: MajorCategoryKey | '') => {
  switch (category) {
    case 'banking':
      return BANKING_TYPES;
    case 'investment':
      return INVESTMENT_TYPES;
    case 'retirement':
      return RETIREMENT_TYPES;
    case 'other_financial':
      return OTHER_FINANCIAL_TYPES;
    case 'properties':
      return PROPERTY_TYPES;
    case 'personal':
      return PERSONAL_TYPES;
    case 'debts':
      return DEBT_TYPES;
    case 'monthly_expenses':
      return EXPENSE_TYPES;
    case 'income_sources':
      return INCOME_SOURCE_TYPES;
    default:
      return [];
  }
};

/* Simple icon for the categories stat card */
const CategoriesIcon = ({ className = 'w-6 h-6 text-cyan-700' }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="3" y="4" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="14" y="4" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" strokeWidth="2" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" strokeWidth="2" />
  </svg>
);

/**
 * Personal items support a second type layer.
 * Only some personal categories expose subtype options.
 */
const getSubTypeOptions = (category: MajorCategoryKey | '', selectedType: string) => {
  if (category !== 'personal') return [];
  if (selectedType === 'physical_assets') return PHYSICAL_ASSET_SUBTYPES;
  if (selectedType === 'digital_assets') return DIGITAL_SUBTYPES;
  if (selectedType === 'collectibles') return COLLECTIBLE_SUBTYPES;
  return [];
};

/* Resolve display labels from the shared option arrays. */
const findLabel = (options: { value: string; label: string }[], value?: string) =>
  options.find((o) => o.value === value)?.label || value || '';

/* Normalizes numeric input values before saving. */
const parseNumber = (value: string) => {
  const cleaned = value.replace(/,/g, '').trim();
  return cleaned ? parseFloat(cleaned) : undefined;
};

/* Read local backup arrays when session-backed values are missing */
const loadBackupArray = <T,>(key: string): T[] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const getAttachedDocuments = (item: OverviewItem): AppDocument[] => {
  switch (item.categoryKey) {
    case 'banking':
      return normalizeDocuments((item.raw as CashAccount).documents || []);
    case 'investment':
      return normalizeDocuments((item.raw as InvestmentAccount).documents || []);
    case 'retirement':
      return normalizeDocuments((item.raw as RetirementAccount).documents || []);
    case 'other_financial':
      return normalizeDocuments((item.raw as OtherFinancialAsset).documents || []);
    case 'properties':
      return normalizeDocuments((item.raw as Property).documents || []);
    case 'personal':
      return normalizeDocuments((item.raw as PersonalItem).documents || []);
    case 'debts':
      return normalizeDocuments((item.raw as DebtItem).documents || []);
    case 'monthly_expenses':
      return normalizeDocuments((item.raw as ExpenseItem).documents || []);
    case 'income_sources':
      return normalizeDocuments((item.raw as IncomeSourceItem).documents || []);
    default:
      return [];
    }
};

/* Match a consistent icon style to each major category */
const getMajorCategoryIcon = (
  category: MajorCategoryKey,
  className = 'w-6 h-6 text-cyan-700'
) => {
  switch (category) {
    case 'banking':
      return <BankIcon className={className} />;
    case 'investment':
      return <LightBulbIcon className={className} />;
    case 'retirement':
      return <ShieldIcon className={className} />;
    case 'other_financial':
      return <ImportantDocumentsIcon className={className} />;
    case 'properties':
      return <HomeIcon className={className} />;
    case 'personal':
      return <PhysicalAssetsIcon className={className} />;
    case 'debts':
      return <CreditCardIcon className={className} />;
    case 'monthly_expenses':
      return <SubscriptionIcon className={className} />;
    case 'income_sources':
      return <BankIcon className={className} />;
    default:
      return <DocumentsIcon className={className} />;
  }
};

/**
 * Picks the list icon using the normalized overview item plus its raw backing type.
 * This keeps the overview renderer generic while still allowing category-specific icons.
 */
const getOverviewItemIcon = (
  item: OverviewItem,
  className = 'w-6 h-6 text-cyan-700'
) => {
  if (item.categoryKey === 'debts') {
    const debt = item.raw as DebtItem;
    switch (debt.category) {
      case 'credit_card':
        return <CreditCardIcon className={className} />;
      case 'personal_auto':
        return <CarIcon className={className} />;
      case 'mortgage':
        return <HomeIcon className={className} />;
      case 'medical':
        return <MedicalIcon className={className} />;
      case 'tax':
        return <TaxIcon className={className} />;
      default:
        return <DocumentsIcon className={className} />;
    }
  }

  if (item.categoryKey === 'monthly_expenses') {
    const expense = item.raw as ExpenseItem;
    switch (expense.category) {
      case 'credit_card':
        return <CreditCardIcon className={className} />;
      case 'insurance':
        return <ShieldIcon className={className} />;
      case 'subscriptions':
        return <SubscriptionIcon className={className} />;
      case 'transportation':
        return <CarIcon className={className} />;
      case 'healthcare':
        return <MedicalIcon className={className} />;
      default:
        return <DocumentsIcon className={className} />;
    }
  }

  if (item.categoryKey === 'personal') {
    const personal = item.raw as PersonalItem;
    switch (personal.category) {
      case 'physical_assets':
        return <PhysicalAssetsIcon className={className} />;
      case 'collectibles':
        return <CollectiblesAndValuablesIcon className={className} />;
      case 'electronics':
        return <ElectronicsIcon className={className} />;
      case 'digital_assets':
        return <NewDigitalAssetsIcon className={className} />;
      case 'custom':
        return <CustomItemIcon className={className} />;
      default:
        return <DocumentsIcon className={className} />;
    }
  }

  if (item.categoryKey === 'properties') {
    return <HomeIcon className={className} />;
  }

  if (item.categoryKey === 'banking') {
    return <BankIcon className={className} />;
  }

  if (item.categoryKey === 'investment') {
    return <LightBulbIcon className={className} />;
  }

  if (item.categoryKey === 'retirement') {
    return <ShieldIcon className={className} />;
  }

  if (item.categoryKey === 'other_financial') {
    return <ImportantDocumentsIcon className={className} />;
  }

  return <DocumentsIcon className={className} />;
};

/**
 * Frame143 is the shared asset inventory page.
 * It hydrates category-specific state from session/local backups, normalizes it into
 * one overview list, and uses a single modal for create/edit across all categories.
 */
const Frame143: React.FC = () => {
  const { session, updateTaskProgress } = useSessionPersistence();

  const [contacts, setContacts] = useState<UnifiedContact[]>([]);
  const [loading, setLoading] = useState(true);

  // Category-specific stores mirror the persisted taskProgress structure.
  const [cashAccounts, setCashAccounts] = useState<CashAccount[]>([]);
  const [investmentAccounts, setInvestmentAccounts] = useState<InvestmentAccount[]>([]);
  const [retirementAccounts, setRetirementAccounts] = useState<RetirementAccount[]>([]);
  const [otherFinancialAssets, setOtherFinancialAssets] = useState<OtherFinancialAsset[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [personalItems, setPersonalItems] = useState<PersonalItem[]>([]);
  const [debts, setDebts] = useState<DebtItem[]>([]);
  const [expenses, setExpenses] = useState<ExpenseItem[]>([]);
  const [incomeSources, setIncomeSources] = useState<IncomeSourceItem[]>([]);

  const [selectedFilter, setSelectedFilter] = useState<FilterKey>('all');

  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // edit/create share the same modal; null means create mode.
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  // Modal state is intentionally flattened so the same save path can build different item shapes.
  const [selectedCategory, setSelectedCategory] = useState<MajorCategoryKey | ''>('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedSubType, setSelectedSubType] = useState('');

  const [name, setName] = useState('');
  const [institution, setInstitution] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [balance, setBalance] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [maturityDate, setMaturityDate] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [beneficiaryIds, setBeneficiaryIds] = useState<string[]>([]);
  const [notes, setNotes] = useState('');

  const [address, setAddress] = useState('');
  const [mortgageBalance, setMortgageBalance] = useState('');

  const [description, setDescription] = useState('');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [location, setLocation] = useState('');
  const [handlingInstructions, setHandlingInstructions] = useState('');

  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [frequency, setFrequency] = useState<ExpenseItem['frequency']>('monthly');
  const [incomeFrequency, setIncomeFrequency] = useState<IncomeSourceItem['frequency']>('monthly');
  const [approximateAmount, setApproximateAmount] = useState('');
  const [autopay, setAutopay] = useState(false);
  const [provider, setProvider] = useState('');
  const [website, setWebsite] = useState('');

  const [documents, setDocuments] = useState<AppDocument[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);

  /* Hydrate each category from session first, then fallback to local backup */
  useEffect(() => {
    const loadContacts = async () => {
      try {
        const allContacts = await unifiedContactService.getAllContacts();
        setContacts(allContacts);
      } catch (error) {
        console.error('[Frame143] Failed to load contacts:', error);
      }
    };

    const financialAnswers = session?.taskProgress?.financial_assets?.answers || {};
    const propertiesAnswers = session?.taskProgress?.properties?.answers || {};
    const personalAnswers = session?.taskProgress?.personal?.answers || {};
    const debtsAnswers = session?.taskProgress?.debts?.answers || {};
    const monthlyAnswers = session?.taskProgress?.monthly_expenses?.answers || {};

    setCashAccounts(
      (financialAnswers.cashAccounts as CashAccount[])?.length
        ? (financialAnswers.cashAccounts as CashAccount[]).map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : loadBackupArray<CashAccount>('cash-accounts-backup').map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
    );

    setInvestmentAccounts(
      (financialAnswers.investmentAccounts as InvestmentAccount[])?.length
        ? (financialAnswers.investmentAccounts as InvestmentAccount[]).map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : loadBackupArray<InvestmentAccount>('investment-accounts-backup').map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
    );

    setRetirementAccounts(
      (financialAnswers.retirementAccounts as RetirementAccount[])?.length
        ? (financialAnswers.retirementAccounts as RetirementAccount[]).map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : loadBackupArray<RetirementAccount>('retirement-accounts-backup').map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
    );

    setOtherFinancialAssets(
      (financialAnswers.otherFinancialAssets as OtherFinancialAsset[])?.length
        ? (financialAnswers.otherFinancialAssets as OtherFinancialAsset[]).map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : loadBackupArray<OtherFinancialAsset>('other-financial-assets-backup').map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
    );

    setProperties(
      (propertiesAnswers.properties as Property[])?.length
        ? (propertiesAnswers.properties as Property[]).map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : loadBackupArray<Property>('properties-backup').map((item) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
    );

    const sessionPersonalItems =
      (personalAnswers.personalItems as Array<PersonalItem | { category?: string }>) || [];
    const backupPersonalItems =
      loadBackupArray<Array<PersonalItem | { category?: string }>[number]>('personal-items-backup');

    /* Older saved data may still contain document entries from another page */
    const filteredPersonalItems = (
      sessionPersonalItems.length ? sessionPersonalItems : backupPersonalItems
    )
      .filter((item) => item.category !== 'documents')
      .map((item) => ({
        ...item,
        documents: normalizeDocuments((item as PersonalItem).documents || []),
      })) as PersonalItem[];

    setPersonalItems(filteredPersonalItems);

    // Debts/expenses currently hydrate from session state only.
    setDebts(
      ((debtsAnswers.debts as DebtItem[]) || []).map((item) => ({
        ...item,
        documents: normalizeDocuments(item.documents || []),
      }))
    );

    setExpenses(
      ((monthlyAnswers.expenses as ExpenseItem[]) || []).map((item) => ({
        ...item,
        documents: normalizeDocuments(item.documents || []),
      }))
    );

    setIncomeSources(
      (financialAnswers.incomeSources as IncomeSourceItem[])?.length
        ? (financialAnswers.incomeSources as IncomeSourceItem[]).map((src) => ({
            ...src,
            documents: normalizeDocuments(src.documents || []),
          }))
        : loadBackupArray<IncomeSourceItem>('income-sources-backup').map((src) => ({
            ...src,
            documents: normalizeDocuments(src.documents || []),
          }))
    );

    loadContacts().finally(() => setLoading(false));
  }, [
    session?.taskProgress?.financial_assets,
    session?.taskProgress?.properties,
    session?.taskProgress?.personal,
    session?.taskProgress?.debts,
    session?.taskProgress?.monthly_expenses,
  ]);

  /* Clear form state before opening add flow or after closing modal */
  const resetForm = () => {
    setEditingItemId(null);
    setSelectedCategory('');
    setSelectedType('');
    setSelectedSubType('');

    setName('');
    setInstitution('');
    setAccountNumber('');
    setRoutingNumber('');
    setBalance('');
    setInterestRate('');
    setMaturityDate('');
    setPolicyNumber('');
    setBeneficiaryIds([]);
    setNotes('');

    setAddress('');
    setMortgageBalance('');

    setDescription('');
    setEstimatedValue('');
    setLocation('');
    setHandlingInstructions('');

    setAmount('');
    setDueDate('');
    setFrequency('monthly');
    setAutopay(false);
    setProvider('');
    setWebsite('');

    setDocuments([]);
    setIsUploadingDocuments(false);
    setIncomeFrequency('monthly');
    setApproximateAmount('');
  };

  /**
   * Writes updates back to the correct category store and taskProgress bucket.
   * financial_assets is split across multiple local arrays but persisted under one task.
   */
  const persistCategoryData = async (category: MajorCategoryKey, updatedData: unknown[]) => {
    switch (category) {
      case 'banking':
        setCashAccounts(updatedData as CashAccount[]);
        localStorage.setItem('cash-accounts-backup', JSON.stringify(updatedData));
        await updateTaskProgress('financial_assets', { cashAccounts: updatedData });
        break;
      case 'investment':
        setInvestmentAccounts(updatedData as InvestmentAccount[]);
        localStorage.setItem('investment-accounts-backup', JSON.stringify(updatedData));
        await updateTaskProgress('financial_assets', { investmentAccounts: updatedData });
        break;
      case 'retirement':
        setRetirementAccounts(updatedData as RetirementAccount[]);
        localStorage.setItem('retirement-accounts-backup', JSON.stringify(updatedData));
        await updateTaskProgress('financial_assets', { retirementAccounts: updatedData });
        break;
      case 'other_financial':
        setOtherFinancialAssets(updatedData as OtherFinancialAsset[]);
        localStorage.setItem('other-financial-assets-backup', JSON.stringify(updatedData));
        await updateTaskProgress('financial_assets', { otherFinancialAssets: updatedData });
        break;
      case 'properties':
        setProperties(updatedData as Property[]);
        localStorage.setItem('properties-backup', JSON.stringify(updatedData));
        await updateTaskProgress('properties', { properties: updatedData });
        break;
      case 'personal':
        setPersonalItems(updatedData as PersonalItem[]);
        localStorage.setItem('personal-items-backup', JSON.stringify(updatedData));
        await updateTaskProgress('personal', { personalItems: updatedData });
        break;
      case 'debts':
        setDebts(updatedData as DebtItem[]);
        await updateTaskProgress('debts', { debts: updatedData }, 'in-progress');
        break;
      case 'monthly_expenses':
        setExpenses(updatedData as ExpenseItem[]);
        await updateTaskProgress('monthly_expenses', { expenses: updatedData }, 'in-progress');
        break;
      case 'income_sources':
        setIncomeSources(updatedData as IncomeSourceItem[]);
        localStorage.setItem('income-sources-backup', JSON.stringify(updatedData));
        await updateTaskProgress('financial_assets', { incomeSources: updatedData });
        break;
      default:
        break;
    }
  };

  /* Returns the active source array for the selected category. */
  const getCurrentCategoryItems = (category: MajorCategoryKey): CategoryItem[] => {
    switch (category) {
      case 'banking':
        return cashAccounts;
      case 'investment':
        return investmentAccounts;
      case 'retirement':
        return retirementAccounts;
      case 'other_financial':
        return otherFinancialAssets;
      case 'properties':
        return properties;
      case 'personal':
        return personalItems;
      case 'debts':
        return debts;
      case 'monthly_expenses':
        return expenses;
      case 'income_sources':
        return incomeSources;
      default:
        return [];
    }
  };

  /* Flatten all category-specific shapes into one list for filtering and rendering */
  const overviewItems: OverviewItem[] = useMemo(() => {
    const bankingItems: OverviewItem[] = cashAccounts.map((account) => ({
      id: account.id,
      categoryKey: 'banking',
      categoryLabel: CATEGORY_LABELS.banking,
      typeLabel: findLabel(BANKING_TYPES, account.type),
      title: account.name,
      subtitle: account.institution,
      details: [
        account.accountNumber ? `Account ending in ${account.accountNumber.slice(-4)}` : '',
        account.balance !== undefined ? `Balance: $${account.balance.toLocaleString()}` : '',
        account.interestRate !== undefined ? `Interest: ${account.interestRate}%` : '',
        account.maturityDate ? `Matures: ${account.maturityDate}` : '',
      ].filter(Boolean),
      notes: account.notes,
      documentsCount: account.documents?.length || 0,
      raw: account,
    }));

    const investmentItems: OverviewItem[] = investmentAccounts.map((account) => ({
      id: account.id,
      categoryKey: 'investment',
      categoryLabel: CATEGORY_LABELS.investment,
      typeLabel: findLabel(INVESTMENT_TYPES, account.type),
      title: account.name,
      subtitle: account.institution,
      details: [
        account.accountNumber ? `Account ending in ${account.accountNumber.slice(-4)}` : '',
      ].filter(Boolean),
      notes: account.notes,
      documentsCount: account.documents?.length || 0,
      raw: account,
    }));

    const retirementItems: OverviewItem[] = retirementAccounts.map((account) => ({
      id: account.id,
      categoryKey: 'retirement',
      categoryLabel: CATEGORY_LABELS.retirement,
      typeLabel: findLabel(RETIREMENT_TYPES, account.type),
      title: account.name,
      subtitle: account.institution,
      details: [
        account.accountNumber ? `Account ending in ${account.accountNumber.slice(-4)}` : '',
        account.beneficiaries?.length ? `Beneficiaries: ${account.beneficiaries.map((b) => b.name).join(', ')}` : '',
      ].filter(Boolean),
      notes: account.notes,
      documentsCount: account.documents?.length || 0,
      raw: account,
    }));

    const otherFinancialItems: OverviewItem[] = otherFinancialAssets.map((asset) => ({
      id: asset.id,
      categoryKey: 'other_financial',
      categoryLabel: CATEGORY_LABELS.other_financial,
      typeLabel: findLabel(OTHER_FINANCIAL_TYPES, asset.type),
      title: asset.name,
      subtitle: asset.institution || 'No provider listed',
      details: [
        asset.policyNumber ? `Policy / Account: ${asset.policyNumber}` : '',
        asset.beneficiaries?.length ? `Beneficiaries: ${asset.beneficiaries.map((b) => b.name).join(', ')}` : '',
      ].filter(Boolean),
      notes: asset.notes,
      documentsCount: asset.documents?.length || 0,
      raw: asset,
    }));

    const propertyItems: OverviewItem[] = properties.map((property) => ({
      id: property.id,
      categoryKey: 'properties',
      categoryLabel: CATEGORY_LABELS.properties,
      typeLabel: findLabel(PROPERTY_TYPES, property.type),
      title: property.displayName || property.address,
      subtitle: property.address,
      details: [
        `Mortgage Balance: $${(property.mortgageBalance || 0).toLocaleString()}`,
      ],
      notes: property.notes,
      documentsCount: property.documents?.length || 0,
      raw: property,
    }));

    const personalOverview: OverviewItem[] = personalItems
      .filter((item: PersonalItem | { category?: string }) => item.category !== 'documents')
      .map((item) => {
        const subType = item.assetType || item.digitalType || item.collectibleType || '';
        return {
          id: item.id,
          categoryKey: 'personal',
          categoryLabel: CATEGORY_LABELS.personal,
          typeLabel: `${findLabel(PERSONAL_TYPES, item.category)}${subType ? ` • ${subType.replace(/_/g, ' ')}` : ''}`,
          title: item.name,
          subtitle: item.location || 'No location listed',
          details: [
            item.estimatedValue !== undefined ? `Value: $${item.estimatedValue.toLocaleString()}` : '',
            item.accountNumber ? `Account / ID: ${item.accountNumber}` : '',
            item.handlingInstructions ? `Handling: ${item.handlingInstructions}` : '',
          ].filter(Boolean),
          notes: item.notes || item.description,
          documentsCount: item.documents?.length || 0,
          raw: item,
        };
      });

    const debtItems: OverviewItem[] = debts.map((debt) => ({
      id: debt.id,
      categoryKey: 'debts',
      categoryLabel: CATEGORY_LABELS.debts,
      typeLabel: findLabel(DEBT_TYPES, debt.category),
      title: debt.creditor,
      subtitle: 'Debt / Liability',
      details: [
        debt.amount !== undefined ? `Amount: $${debt.amount.toLocaleString()}` : '',
        debt.accountNumber ? `Account ending in ${debt.accountNumber.slice(-4)}` : '',
        debt.dueDate ? `Due: ${new Date(debt.dueDate).toLocaleDateString()}` : '',
      ].filter(Boolean),
      notes: debt.notes,
      documentsCount: debt.documents?.length || 0,
      raw: debt,
    }));

    const expenseItems: OverviewItem[] = expenses.map((expense) => ({
      id: expense.id,
      categoryKey: 'monthly_expenses',
      categoryLabel: CATEGORY_LABELS.monthly_expenses,
      typeLabel: findLabel(EXPENSE_TYPES, expense.category),
      title: expense.name,
      subtitle: expense.provider || 'Recurring expense',
      details: [
        expense.amount !== undefined ? `$${expense.amount.toLocaleString()} / ${expense.frequency}` : expense.frequency,
        expense.accountNumber ? `Account ending in ${expense.accountNumber.slice(-4)}` : '',
        expense.dueDate ? `Due: ${new Date(expense.dueDate).toLocaleDateString()}` : '',
        expense.autopay ? 'AutoPay Enabled' : '',
      ].filter(Boolean),
      notes: expense.notes,
      documentsCount: expense.documents?.length || 0,
      raw: expense,
    }));

    const incomeSourceItems: OverviewItem[] = incomeSources.map((src) => ({
      id: src.id,
      categoryKey: 'income_sources' as MajorCategoryKey,
      categoryLabel: CATEGORY_LABELS.income_sources,
      typeLabel: findLabel(INCOME_SOURCE_TYPES, src.type),
      title: src.sourceName,
      subtitle: src.payerInstitution,
      details: [
        src.frequency.charAt(0).toUpperCase() + src.frequency.slice(1),
        src.approximateAmount !== undefined
          ? `Approx. $${src.approximateAmount.toLocaleString()}`
          : '',
      ].filter(Boolean),
      notes: src.notes,
      documentsCount: src.documents?.length || 0,
      raw: src,
    }));

    return [
      ...bankingItems,
      ...investmentItems,
      ...retirementItems,
      ...otherFinancialItems,
      ...propertyItems,
      ...personalOverview,
      ...debtItems,
      ...expenseItems,
      ...incomeSourceItems,
    ];
  }, [
    cashAccounts,
    investmentAccounts,
    retirementAccounts,
    otherFinancialAssets,
    properties,
    personalItems,
    debts,
    expenses,
    incomeSources,
  ]);

  /* Apply the selected dropdown filter to the flattened overview list */
  const filteredItems = useMemo(() => {
    if (selectedFilter === 'all') return overviewItems;
    return overviewItems.filter((item) => item.categoryKey === selectedFilter);
  }, [overviewItems, selectedFilter]);

  /* Counts shown in the filter dropdown and summary area */
  const categoryCounts = useMemo(() => {
    return {
      banking: cashAccounts.length,
      investment: investmentAccounts.length,
      retirement: retirementAccounts.length,
      other_financial: otherFinancialAssets.length,
      properties: properties.length,
      personal: personalItems.length,
      debts: debts.length,
      monthly_expenses: expenses.length,
      income_sources: incomeSources.length,
    };
  }, [
    cashAccounts.length,
    investmentAccounts.length,
    retirementAccounts.length,
    otherFinancialAssets.length,
    properties.length,
    personalItems.length,
    debts.length,
    expenses.length,
    incomeSources.length,
  ]);

  const totalAccounts = overviewItems.length;
  const totalCategories = Object.values(categoryCounts).filter((count) => count > 0).length;

  /* Summary cards at the top of the page */
  const statCards = [
    {
      label: 'Total Accounts',
      value: totalAccounts,
      icon: <BankIcon className="w-6 h-6 text-cyan-700" />,
    },
    {
      label: 'Categories',
      value: totalCategories,
      icon: <CategoriesIcon className="w-6 h-6 text-cyan-700" />,
    },
  ];

  /* Open modal in create mode */
  const handleOpenModal = () => {
    resetForm();
    setShowModal(true);
  };

  /**
   * Loads an existing item into the shared modal.
   * Edit mode reuses the same form state and save pipeline as create mode.
   */
  const handleEditItem = (item: OverviewItem) => {
    resetForm();
    setEditingItemId(item.id);
    setSelectedCategory(item.categoryKey);

    switch (item.categoryKey) {
      case 'banking': {
        const raw = item.raw as CashAccount;
        setSelectedType(raw.type);
        setName(raw.name);
        setInstitution(raw.institution);
        setAccountNumber(raw.accountNumber);
        setRoutingNumber(raw.routingNumber || '');
        setBalance(raw.balance?.toString() || '');
        setInterestRate(raw.interestRate?.toString() || '');
        setMaturityDate(raw.maturityDate || '');
        setBeneficiaryIds(raw.beneficiaries?.map((b) => b.contactId) || []);
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      case 'investment': {
        const raw = item.raw as InvestmentAccount;
        setSelectedType(raw.type);
        setName(raw.name);
        setInstitution(raw.institution);
        setAccountNumber(raw.accountNumber);
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      case 'retirement': {
        const raw = item.raw as RetirementAccount;
        setSelectedType(raw.type);
        setName(raw.name);
        setInstitution(raw.institution);
        setAccountNumber(raw.accountNumber);
        setBeneficiaryIds(raw.beneficiaries?.map((b) => b.contactId) || []);
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      case 'other_financial': {
        const raw = item.raw as OtherFinancialAsset;
        setSelectedType(raw.type);
        setName(raw.name);
        setInstitution(raw.institution || '');
        setPolicyNumber(raw.policyNumber || '');
        setBeneficiaryIds(raw.beneficiaries?.map((b) => b.contactId) || []);
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      case 'properties': {
        const raw = item.raw as Property;
        setSelectedType(raw.type);
        setAddress(raw.address);
        setMortgageBalance(raw.mortgageBalance?.toString() || '');
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      case 'personal': {
        const raw = item.raw as PersonalItem;
        setSelectedType(raw.category);
        setSelectedSubType(raw.assetType || raw.digitalType || raw.collectibleType || '');
        setName(raw.name);
        setDescription(raw.description);
        setEstimatedValue(raw.estimatedValue?.toString() || '');
        setAccountNumber(raw.accountNumber || '');
        setLocation(raw.location || '');
        setHandlingInstructions(raw.handlingInstructions || '');
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      case 'debts': {
        const raw = item.raw as DebtItem;
        setSelectedType(raw.category);
        setName(raw.creditor);
        setAmount(raw.amount?.toString() || '');
        setAccountNumber(raw.accountNumber || '');
        setDueDate(raw.dueDate || '');
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      case 'monthly_expenses': {
        const raw = item.raw as ExpenseItem;
        setSelectedType(raw.category);
        setName(raw.name);
        setAmount(raw.amount?.toString() || '');
        setFrequency(raw.frequency || 'monthly');
        setDueDate(raw.dueDate || '');
        setAccountNumber(raw.accountNumber || '');
        setAutopay(raw.autopay || false);
        setProvider(raw.provider || '');
        setWebsite(raw.website || '');
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      case 'income_sources': {
        const raw = item.raw as IncomeSourceItem;
        setSelectedType(raw.type);
        setName(raw.sourceName);
        setInstitution(raw.payerInstitution);
        setIncomeFrequency(raw.frequency);
        setApproximateAmount(raw.approximateAmount?.toString() || '');
        setNotes(raw.notes || '');
        setDocuments(normalizeDocuments(raw.documents || []));
        break;
      }
      default:
        break;
    }

    setShowModal(true);
  };

  /* Delete the selected item from the correct category store */
  const handleDeleteItem = async (item: OverviewItem) => {
    if (!confirm('Are you sure you want to delete this item?')) return;

    try {
      const attachedDocuments = getAttachedDocuments(item);
      for (const doc of attachedDocuments) {
        try {
          await documentService.delete(doc.id);
        } catch (error) {
          console.error('[Frame143] Failed to delete document while deleting item:', error);
        }
      }

      const currentItems = getCurrentCategoryItems(item.categoryKey);
      const updatedItems = currentItems.filter((entry: unknown) => entry.id !== item.id);

      // Cascade: remove distribution record for the deleted item.
      const currentDist = (session.taskProgress?.assetDistribution?.answers?.distributions ?? {}) as DistributionMap;
      if (currentDist[item.id]) {
        const cleanedDist = removeAssetDistribution(currentDist, item.id);
        await updateTaskProgress('assetDistribution', { distributions: cleanedDist });
      }

      await persistCategoryData(item.categoryKey, updatedItems);
    } catch (error) {
      console.error('[Frame143] Failed to delete item:', error);
      alert('Failed to delete item. Please try again.');
    }
  };



  /**
   * Validates modal state, builds the category-specific payload,
   * then persists it through the shared category writer.
   */
  const handleSave = async () => {
    if (!selectedCategory) {
      alert('Please select a category');
      return;
    }

    if (!selectedType) {
      alert('Please select a type');
      return;
    }

    if (
      selectedCategory === 'personal' &&
      getSubTypeOptions(selectedCategory, selectedType).length > 0 &&
      !selectedSubType
    ) {
      alert('Please select a subtype');
      return;
    }

    if (
      (selectedCategory === 'banking' && (!name || !institution || !accountNumber)) ||
      (selectedCategory === 'investment' && (!name || !institution || !accountNumber)) ||
      (selectedCategory === 'retirement' && (!name || !institution || !accountNumber)) ||
      (selectedCategory === 'other_financial' && (!name || !institution || !policyNumber)) ||
      (selectedCategory === 'properties' && !address) ||
      (selectedCategory === 'personal' && !name) ||
      (selectedCategory === 'debts' && !name) ||
      (selectedCategory === 'monthly_expenses' && !name) ||
      (selectedCategory === 'income_sources' && (!name || !institution))
    ) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const beneficiaryData = beneficiaryIds
        .map((id) => {
          const contact = contacts.find((c) => c.id === id);
          return contact
            ? { contactId: id, name: `${contact.firstName} ${contact.lastName}` }
            : null;
        })
        .filter((b): b is Beneficiary => b !== null);

      const currentItems = getCurrentCategoryItems(selectedCategory);

      let newItem: unknown = null;

      switch (selectedCategory) {
        case 'banking':
          newItem = {
            id: editingItemId || Date.now().toString(),
            type: selectedType as CashAccount['type'],
            name: name.trim(),
            institution: institution.trim(),
            accountNumber: accountNumber.trim(),
            routingNumber: routingNumber.trim() || undefined,
            balance: parseNumber(balance) ?? 0,
            interestRate: parseNumber(interestRate),
            maturityDate: maturityDate || undefined,
            beneficiaries: beneficiaryData.length ? beneficiaryData : undefined,
            notes: notes.trim() || undefined,
            documents,
          } satisfies CashAccount;
          break;

        case 'investment':
          newItem = {
            id: editingItemId || Date.now().toString(),
            type: selectedType as InvestmentAccount['type'],
            name: name.trim(),
            institution: institution.trim(),
            accountNumber: accountNumber.trim(),
            notes: notes.trim() || undefined,
            documents,
          } satisfies InvestmentAccount;
          break;

        case 'retirement':
          newItem = {
            id: editingItemId || Date.now().toString(),
            type: selectedType as RetirementAccount['type'],
            name: name.trim(),
            institution: institution.trim(),
            accountNumber: accountNumber.trim(),
            beneficiaries: beneficiaryData.length ? beneficiaryData : undefined,
            notes: notes.trim() || undefined,
            documents,
          } satisfies RetirementAccount;
          break;

        case 'other_financial':
          newItem = {
            id: editingItemId || Date.now().toString(),
            type: selectedType as OtherFinancialAsset['type'],
            name: name.trim(),
            institution: institution.trim() || undefined,
            policyNumber: policyNumber.trim() || undefined,
            beneficiaries: beneficiaryData.length ? beneficiaryData : undefined,
            notes: notes.trim() || undefined,
            documents,
          } satisfies OtherFinancialAsset;
          break;

        case 'properties':
          newItem = {
            id: editingItemId || Date.now().toString(),
            type: selectedType as Property['type'],
            address: address.trim(),
            mortgageBalance: parseNumber(mortgageBalance) ?? 0,
            notes: notes.trim() || undefined,
            displayName: address.trim(),
            documents,
          } satisfies Property;
          break;

        case 'personal':
          newItem = {
            id: editingItemId || Date.now().toString(),
            category: selectedType as PersonalItem['category'],
            name: name.trim(),
            description: description.trim(),
            estimatedValue: parseNumber(estimatedValue),
            accountNumber: accountNumber.trim() || undefined,
            location: location.trim(),
            handlingInstructions: handlingInstructions.trim(),
            notes: notes.trim(),
            createdAt: new Date().toISOString(),
            documents,
            ...(selectedType === 'physical_assets' ? { assetType: selectedSubType as PersonalItem['assetType'] } : {}),
            ...(selectedType === 'digital_assets' ? { digitalType: selectedSubType as PersonalItem['digitalType'] } : {}),
            ...(selectedType === 'collectibles' ? { collectibleType: selectedSubType as PersonalItem['collectibleType'] } : {}),
          } satisfies PersonalItem;
          break;

        case 'debts':
          newItem = {
            id: editingItemId || Date.now().toString(),
            category: selectedType as DebtItem['category'],
            creditor: name.trim(),
            amount: parseNumber(amount),
            accountNumber: accountNumber.trim() || undefined,
            dueDate: dueDate || undefined,
            notes: notes.trim() || undefined,
            documents,
          } satisfies DebtItem;
          break;

        case 'monthly_expenses':
          newItem = {
            id: editingItemId || Date.now().toString(),
            category: selectedType as ExpenseItem['category'],
            name: name.trim(),
            amount: parseNumber(amount),
            frequency,
            dueDate: dueDate || undefined,
            accountNumber: accountNumber.trim() || undefined,
            autopay,
            provider: provider.trim() || undefined,
            website: website.trim() || undefined,
            notes: notes.trim() || undefined,
            documents,
          } satisfies ExpenseItem;
          break;

        case 'income_sources':
          newItem = {
            id: editingItemId || Date.now().toString(),
            type: selectedType as IncomeSourceItem['type'],
            sourceName: name.trim(),
            payerInstitution: institution.trim(),
            frequency: incomeFrequency,
            approximateAmount: parseNumber(approximateAmount),
            notes: notes.trim() || undefined,
            documents,
          } satisfies IncomeSourceItem;
          break;
      }

      const updatedItems = editingItemId
        ? currentItems.map((entry) => (entry.id === editingItemId ? newItem : entry))
        : [...currentItems, newItem];

      // Sync distribution record: recalculate equal-split percentages for all
      // current beneficiaries. Runs for both new and edited items in categories
      // that support beneficiary assignment.
      if (['banking', 'retirement', 'other_financial'].includes(selectedCategory)) {
        const assetId = editingItemId || (newItem as { id: string }).id;
        const currentDist = (session.taskProgress?.assetDistribution?.answers?.distributions ?? {}) as DistributionMap;
        const syncedDist = autoSyncDistributionForAsset(currentDist, assetId, beneficiaryData);
        await updateTaskProgress('assetDistribution', { distributions: syncedDist });
      }

      await persistCategoryData(selectedCategory, updatedItems);
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('[Frame143] Failed to save item:', error);
      alert('Failed to save item. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame143] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const removeDocumentFromForm = async (index: number) => {
    const docToRemove = documents[index];
    if (!docToRemove) return;

    try {
      await documentService.delete(docToRemove.id);
    } catch (error) {
      console.error('[Frame143] Failed to delete unsaved document:', error);
      alert('Failed to delete document.');
      return;
    }

    setDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const typeOptions = getTypeOptions(selectedCategory);
  const subTypeOptions = getSubTypeOptions(selectedCategory, selectedType);

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <div className="w-full max-w-[835px]">
          {/* Header and primary add action */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1">
              <ResponsiveHeader title="What You Own" />
            </div>
            <div className="shrink-0">
              <button
                onClick={handleOpenModal}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-700 text-white text-sm font-medium rounded-md hover:bg-cyan-800 transition-colors whitespace-nowrap"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Account
              </button>
            </div>
          </div>

          {/* Intro copy */}
          <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
            Inventory your financial and physical assets, from bank accounts and retirement plans to property, debts, valuables, and recurring expenses.
          </p>

          {/* Summary cards */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-md border border-stone-200 p-6"
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0">{card.icon}</div>

                  <div className="flex flex-col">
                    <div className="font-sans text-base text-stone-600">{card.label}</div>
                    <div className="font-sans text-3xl font-medium text-stone-800 leading-none mt-4">
                      {loading ? '—' : card.value}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Combined accounts and assets list */}
          <div className="mt-7">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-3">
              <h2 className="font-sans text-xl font-medium text-stone-800">
                All Accounts & Assets
              </h2>

              <div className="shrink-0">
                <select
                  value={selectedFilter}
                  onChange={(e) => setSelectedFilter(e.target.value as FilterKey)}
                  className="min-w-[220px] px-3 py-2 border border-stone-300 rounded-md bg-white text-sm text-stone-800"
                >
                  <option value="all">All ({totalAccounts})</option>
                  {MAJOR_CATEGORIES.map((category) => (
                    <option key={category.key} value={category.key}>
                      {category.label} ({categoryCounts[category.key]})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12 text-stone-500">
                  <div className="inline-block w-6 h-6 border-2 border-cyan-700 border-t-transparent rounded-full animate-spin mb-2" />
                  <p className="font-sans text-sm">Loading accounts and assets...</p>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-stone-300 rounded-lg">
                  <p className="font-sans text-stone-500 text-sm">
                    No items found for this filter yet.
                  </p>
                </div>
              ) : (
                filteredItems.map((item) => (
                  <div
                    key={`${item.categoryKey}-${item.id}`}
                    className="flex flex-col sm:flex-row sm:items-start gap-4 bg-white rounded-lg border border-stone-200 p-6"
                  >
                    {/* Item icon */}
                    <div className="shrink-0 mt-0.5">
                      {getOverviewItemIcon(item, 'w-6 h-6 text-cyan-700')}
                    </div>

                    {/* Main item content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-sans text-lg font-medium text-stone-800">
                          {item.title}
                        </div>
                        <span className="inline-flex items-center rounded-full bg-stone-200 text-stone-800 px-2.5 py-1 text-xs font-medium">
                          {item.categoryLabel}
                        </span>
                      </div>

                      <div className="font-sans text-base text-stone-600 mt-1">
                        {item.typeLabel}
                      </div>

                      {item.subtitle && (
                        <div className="font-sans text-base text-stone-600 mt-1">
                          {item.subtitle}
                        </div>
                      )}

                      {item.details.length > 0 && (
                        <div className="flex flex-wrap gap-x-5 gap-y-1 font-sans text-base text-stone-600 mt-1">
                          {item.details.map((detail, index) => (
                            <span key={index}>{detail}</span>
                          ))}
                        </div>
                      )}

                      {item.notes && (
                        <div className="font-sans text-sm text-stone-500 mt-2">
                          {item.notes}
                        </div>
                      )}

                      {(() => {
                        const attachedDocuments = getAttachedDocuments(item);

                        return attachedDocuments.length > 0 ? (
                          <div className="mt-3 space-y-2">
                            <div className="font-sans text-sm text-cyan-700">
                              {attachedDocuments.length} document(s) attached
                            </div>

                            <div className="flex flex-col gap-2">
                              {attachedDocuments.map((doc, index) => (
                                <div
                                  key={`${doc.id}-${index}`}
                                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2"
                                >
                                  <button
                                    type="button"
                                    onClick={() => documentService.open(doc.id)}
                                    className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-sm font-medium text-stone-800 hover:text-cyan-700 transition-colors text-left"
                                  >
                                    {selectedCategory
                                      ? getMajorCategoryIcon(selectedCategory, 'w-4 h-4 text-cyan-700 shrink-0 mt-0.5')
                                      : <DocumentsIcon className="w-4 h-4 text-cyan-700 shrink-0 mt-0.5" />}
                                    <span className="text-sm text-stone-600 break-words whitespace-normal min-w-0">
                                      {doc.name}
                                    </span>
                                  </button>

                                  <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => downloadDocument(doc)}
                                      className="text-cyan-600 hover:text-cyan-800 text-xs"
                                    >
                                      Download
                                    </button>

                                    <button
                                      onClick={() => removeDocumentFromForm(index)}
                                      className="text-red-600 hover:text-red-700 text-xs"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}
                    </div>

                    {/* Row actions */}
                    <div className="flex gap-2 shrink-0">
                      <button
                        onClick={() => handleEditItem(item)}
                        className="text-cyan-600 hover:text-cyan-800 px-3 py-1 rounded border border-cyan-400 hover:border-cyan-700 font-sans text-sm font-medium whitespace-nowrap transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item)}
                        className="text-red-600 hover:text-red-800 px-3 py-1 rounded border border-red-300 hover:border-red-700 font-sans text-sm font-medium whitespace-nowrap transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </ResponsiveContent>

      {/* Shared modal for creating and editing assets across all categories */}
      <Modal
        title={editingItemId ? 'Edit Item' : 'Add Account / Asset'}
        open={showModal}
        onOk={handleSave}
        onCancel={() => {
          setShowModal(false);
          resetForm();
        }}
        okText={submitting ? (editingItemId ? 'Updating...' : 'Adding...') : (editingItemId ? 'Update Item' : 'Add Item')}
        cancelText="Cancel"
      >
        <div className="space-y-6">

          {/* Category selector controls which fields appear below */}
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Category *
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value as MajorCategoryKey);
                setSelectedType('');
                setSelectedSubType('');
              }}
              disabled={!!editingItemId}
              className="w-full px-3 py-2 border border-stone-300 rounded-md disabled:bg-stone-100"
            >
              <option value="">Select category</option>
              {MAJOR_CATEGORIES.map((category) => (
                <option key={category.key} value={category.key}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          {/* Type selector */}
          {selectedCategory && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Type *
              </label>
              <select
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                  setSelectedSubType('');
                }}
                className="w-full px-3 py-2 border border-stone-300 rounded-md"
              >
                <option value="">Select type</option>
                {typeOptions.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Personal item subtype selector */}
          {subTypeOptions.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Subtype *
              </label>
              <select
                value={selectedSubType}
                onChange={(e) => setSelectedSubType(e.target.value)}
                className="w-full px-3 py-2 border border-stone-300 rounded-md"
              >
                <option value="">Select subtype</option>
                {subTypeOptions.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Shared account fields for banking, investment, and retirement */}
          {(selectedCategory === 'banking' ||
            selectedCategory === 'investment' ||
            selectedCategory === 'retirement') && (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Account Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Enter account name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Financial Institution *
                </label>
                <input
                  type="text"
                  value={institution}
                  onChange={(e) => setInstitution(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Enter institution"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Account Number *
                </label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Enter account number"
                />
              </div>
            </>
          )}

          {/* Banking-specific fields */}
          {selectedCategory === 'banking' && (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Routing Number
                </label>
                <input
                  type="text"
                  value={routingNumber}
                  onChange={(e) => setRoutingNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Routing number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Current Balance
                </label>
                <input
                  type="text"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="0.00"
                />
              </div>

              {(selectedType === 'savings' || selectedType === 'money_market' || selectedType === 'cd') && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Interest Rate
                  </label>
                  <input
                    type="text"
                    value={interestRate}
                    onChange={(e) => setInterestRate(e.target.value.replace(/[^0-9.]/g, ''))}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md"
                    placeholder="4.25"
                  />
                </div>
              )}

              {selectedType === 'cd' && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Maturity Date
                  </label>
                  <input
                    type="date"
                    value={maturityDate}
                    onChange={(e) => setMaturityDate(e.target.value)}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  />
                </div>
              )}
            </>
          )}

          {/* Beneficiary-capable categories */}
          {selectedCategory === 'retirement' || selectedCategory === 'other_financial' || selectedCategory === 'banking' ? (
            <>
              {selectedCategory === 'other_financial' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Asset Name *
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md"
                      placeholder="Enter asset name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Institution / Provider *
                    </label>
                    <input
                      type="text"
                      value={institution}
                      onChange={(e) => setInstitution(e.target.value)}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md"
                      placeholder="Enter provider"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Policy / Account Number *
                    </label>
                    <input
                      type="text"
                      value={policyNumber}
                      onChange={(e) => setPolicyNumber(e.target.value)}
                      className="w-full px-3 py-2 border border-stone-300 rounded-md"
                      placeholder="Policy or account number"
                    />
                  </div>
                </>
              )}

              {(selectedCategory === 'banking' || selectedCategory === 'retirement' || selectedCategory === 'other_financial') && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Beneficiaries
                  </label>
                  <div className="space-y-2">
                    {beneficiaryIds.map((beneficiaryId, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <select
                          value={beneficiaryId}
                          onChange={(e) => {
                            const updated = [...beneficiaryIds];
                            updated[index] = e.target.value;
                            setBeneficiaryIds(updated);
                          }}
                          className="flex-1 px-3 py-2 border border-stone-300 rounded-md"
                        >
                          <option value="">Select a beneficiary</option>
                          {contacts.map((contact) => (
                            <option key={contact.id} value={contact.id}>
                              {contact.firstName} {contact.lastName}
                              {contact.relationship ? ` (${contact.relationship})` : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => setBeneficiaryIds(beneficiaryIds.filter((_, i) => i !== index))}
                          className="text-red-600 hover:text-red-700 px-3 py-2"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setBeneficiaryIds([...beneficiaryIds, ''])}
                      className="text-cyan-600 hover:text-cyan-700 text-sm font-medium"
                    >
                      + Add Beneficiary
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : null}

          {/* Property-specific fields */}
          {selectedCategory === 'properties' && (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Address *
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Enter property address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Mortgage Balance
                </label>
                <input
                  type="text"
                  value={mortgageBalance}
                  onChange={(e) => setMortgageBalance(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="0.00"
                />
              </div>
            </>
          )}

          {/* Personal asset fields */}
          {selectedCategory === 'personal' && (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Item Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Enter item name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Brief description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Estimated Value
                </label>
                <input
                  type="text"
                  value={estimatedValue}
                  onChange={(e) => setEstimatedValue(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="0.00"
                />
              </div>

              {selectedType === 'digital_assets' && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">
                    Account Number / ID
                  </label>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-stone-300 rounded-md"
                    placeholder="Account identifier"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Where is it located?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Handling Instructions
                </label>
                <textarea
                  value={handlingInstructions}
                  onChange={(e) => setHandlingInstructions(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Special instructions"
                />
              </div>
            </>
          )}

          {/* Debt-specific fields */}
          {selectedCategory === 'debts' && (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Creditor Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Enter creditor name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Estimated Amount
                </label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Enter account number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                />
              </div>
            </>
          )}

          {/* Monthly expense fields */}
          {selectedCategory === 'monthly_expenses' && (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Expense Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Enter expense name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Estimated Amount
                </label>
                <input
                  type="text"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Frequency *
                </label>
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value as ExpenseItem['frequency'])}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annually">Annually</option>
                  <option value="unknown">Unknown</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Account Number
                </label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Enter account number"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="autopay"
                  checked={autopay}
                  onChange={(e) => setAutopay(e.target.checked)}
                  className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                />
                <label htmlFor="autopay" className="ml-2 block text-sm text-stone-700">
                  AutoPay Enabled
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Provider / Company
                </label>
                <input
                  type="text"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="Provider name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="https://..."
                />
              </div>
            </>
          )}

          {/* Income source fields */}
          {selectedCategory === 'income_sources' && (
            <>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Source Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="e.g., SSA Retirement Benefit"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Payer / Institution *
                </label>
                <input
                  type="text"
                  value={institution}
                  onChange={(e) => setInstitution(e.target.value)}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="e.g., Social Security Administration"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Frequency
                </label>
                <select
                  value={incomeFrequency}
                  onChange={(e) => setIncomeFrequency(e.target.value as IncomeSourceItem['frequency'])}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="annual">Annual</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Approx. Amount
                </label>
                <input
                  type="text"
                  value={approximateAmount}
                  onChange={(e) => setApproximateAmount(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-md"
                  placeholder="0.00"
                />
              </div>
            </>
          )}

          {/* Shared notes field */}
          {selectedCategory && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-stone-300 rounded-md"
                placeholder="Any additional information"
              />
            </div>
          )}

          {/* Shared upload area */}
          {selectedCategory && (
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">
                Upload Documents
              </label>
              <FileUpload
                onUpload={async (files) => {
                  if (!files.length) return;

                  setIsUploadingDocuments(true);

                  try {
                    const uploadedDocs = await Promise.all(
                      files.map((file) => documentService.upload(file))
                    );

                    setDocuments((prev) => [...prev, ...uploadedDocs]);
                  } catch (error) {
                    console.error('[Frame143] Failed to upload document(s):', error);
                    alert('Failed to upload one or more documents.');
                  } finally {
                    setIsUploadingDocuments(false);
                  }
                }}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                multiple
              />
              {isUploadingDocuments && (
                <div className="mt-3 text-sm text-stone-500">
                  Uploading documents...
                </div>
              )}
              {documents.length > 0 && (
                <div className="mt-3 space-y-2">
                  {documents.map((doc, index) => (
                    <div
                      key={`${doc.id}-${index}`}
                      className="flex items-center justify-between gap-3 rounded-md border border-stone-200 bg-stone-50 px-3 py-2"
                    >
                      <button
                        type="button"
                        onClick={() => documentService.open(doc.id)}
                        className="flex items-center gap-2 min-w-0 flex-1 text-sm font-medium text-stone-800 hover:text-cyan-700 transition-colors text-left"
                      >
                        {selectedCategory
                          ? getMajorCategoryIcon(selectedCategory, 'w-4 h-4 text-cyan-700')
                          : <DocumentsIcon className="w-4 h-4 text-cyan-700" />}
                        <span className="text-sm text-stone-600 break-all">
                          {doc.name}
                        </span>
                      </button>
                      <button
                        onClick={() => removeDocumentFromForm(index)}
                        className="text-red-600 hover:text-red-700 text-xs shrink-0"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </ResponsiveContainer>
  );
};

export default Frame143;